import type { Persona } from '@projektverwaltung/shared';
import { PERSONA_LABELS } from '@projektverwaltung/shared';
/**
 * V1.5 Persona-Hook + Provider (Phase 9).
 * Quelle: Brief §8 + 05_implementation_session2.md §10.6.
 *
 * Ableitung serverseitig via GET /v1/users/me/personas:
 *   - aus user_function_roles (kaufm_leitung/bereichsleiter/projektleiter/mtb/service_leitung)
 *   - + Manager wenn tags.manager_user_id == me
 *
 * Active Persona persistiert in users.preferences.active_persona via PATCH
 * /v1/users/me/preferences.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, createContext, useCallback, useContext, useMemo } from 'react';
import { api } from './api.js';
import { useSession } from './session.js';

type PersonaContextValue = {
  isLoading: boolean;
  available: Persona[];
  active: Persona;
  setActive: (persona: Persona) => void;
  isPending: boolean;
};

const PersonaContext = createContext<PersonaContextValue | null>(null);

export function PersonaProvider({ children }: { children: ReactNode }) {
  const { user } = useSession();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['my-personas', user?.user_id],
    queryFn: () => api.getMyPersonas(),
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  const mutation = useMutation({
    mutationFn: (persona: Persona) => api.setActivePersona(persona),
    onSuccess: (_, persona) => {
      queryClient.setQueryData(
        ['my-personas', user?.user_id],
        (prev: PersonaContextValue | undefined) => (prev ? { ...prev, active: persona } : prev),
      );
      void queryClient.invalidateQueries({ queryKey: ['my-personas', user?.user_id] });
    },
  });

  const setActive = useCallback((persona: Persona) => mutation.mutate(persona), [mutation]);

  const value = useMemo<PersonaContextValue>(
    () => ({
      isLoading: query.isLoading,
      available: query.data?.available ?? ['kl'],
      active: query.data?.active ?? 'kl',
      setActive,
      isPending: mutation.isPending,
    }),
    [query.isLoading, query.data, setActive, mutation.isPending],
  );

  return <PersonaContext.Provider value={value}>{children}</PersonaContext.Provider>;
}

export function usePersona(): PersonaContextValue {
  const ctx = useContext(PersonaContext);
  if (!ctx) throw new Error('usePersona must be used within <PersonaProvider>');
  return ctx;
}

export function personaLabel(p: Persona): string {
  return PERSONA_LABELS[p];
}
